import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { defaultNS, NAMESPACES, resources } from './resources';

export type { BreadcrumbKey } from './locales/en/breadcrumbs';

export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

// Local-storage key holding the user's language choice. v1 persists locally
// only; profile-level persistence is a deliberate follow-up (see the i18n PRD).
export const LANGUAGE_STORAGE_KEY = 'fleetster-language';

// Registered before init(): with inline resources and a synchronous
// (localStorage-only) detector, init() resolves and emits languageChanged
// synchronously, so a listener added after init() would miss that first
// emission and leave <html lang> on its hardcoded default.
i18n.on('languageChanged', lng => {
  document.documentElement.lang = lng;
});

// English is the product default and fallback: detection reads only the
// stored choice (never `navigator`), so a first visit gets English regardless
// of browser language; every locale is a complete, type-checked dictionary, so the fallback never fills a runtime gap.
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS: [...defaultNS],
    ns: [...NAMESPACES],
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes interpolated values.
      escapeValue: false,
    },
    // The dictionary ships inline and is never lazy-loaded, so translations are
    // ready on first render — no Suspense boundary required.
    react: { useSuspense: false },
  });

// `resolvedLanguage` can be unset early in init, when `language` may still
// hold a raw, unvalidated detector value (e.g. a tampered localStorage entry).
export function currentLanguage(): Language {
  const language = i18n.resolvedLanguage ?? i18n.language;
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language) ? (language as Language) : 'en';
}

export default i18n;
